import { z } from 'zod';
/**
 * Switch-hold self-serve resolution (E-SF07 AC1) — the shape US-Shop's status
 * page sends to `POST /bff/status/:token/new-occupant`.
 *
 * A switch-hold at a premise (e.g. a new-occupant / prior-balance hold) can be
 * self-serve resolved by the customer submitting a New Occupant Statement plus
 * proof of occupancy; the BFF forwards it to the CRM as an ops case
 * (`OpsEnrollmentIssues`). Where the CRM ops-case endpoint is not wired yet, the
 * BFF fails loud (501) via a not-configured seam — it never fabricates a case
 * (US posture; mirrors the E-SF03 out-of-area lead sink).
 *
 * ── REQUEST-ONLY PII ───────────────────────────────────────────────────────
 * The statement + proof carry PII (a prior-occupant name, an uploaded proof
 * document). They are REQUEST-only — no response schema carries them, and the
 * BFF never logs the body. The response returns only an opaque case reference.
 */
/** ISO calendar date (YYYY-MM-DD). */
const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'date must be YYYY-MM-DD');
/**
 * The accepted proof-of-occupancy document types. A narrow allowlist — a lease /
 * deed / utility bill scan is a PDF or an image; nothing executable.
 */
export const OccupancyProofContentType = z.enum([
    'application/pdf',
    'image/jpeg',
    'image/png',
]);
/**
 * Max base64 length of the inline proof document (~30 KB decoded). The BFF's
 * global body limit is 64 KB; a small scan/thumbnail rides inline, and a
 * production direct-to-storage upload lands with the real CRM ops-case endpoint
 * (recorded — deferred, not fabricated).
 */
const MAX_PROOF_BASE64_LEN = 40_000;
/** Standard base64 (with optional `=` padding). Not data-URL prefixed. */
const Base64Schema = z
    .string()
    .min(1)
    .max(MAX_PROOF_BASE64_LEN)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/u, 'proof content must be base64 (no data-URL prefix)');
/** The uploaded proof-of-occupancy document (metadata + inline base64 content). */
export const OccupancyProofDocumentSchema = z.object({
    filename: z.string().min(1).max(200),
    contentType: OccupancyProofContentType,
    /** Base64-encoded document bytes (bounded; see MAX_PROOF_BASE64_LEN). */
    contentBase64: Base64Schema,
});
/**
 * The New Occupant Statement — the customer's affirmation that they are the new
 * occupant of the premise (the standard remedy for a new-occupant switch-hold).
 * `acknowledged` is `literal(true)`: "submitted without affirming" is
 * un-representable (the click-wrap consent precedent).
 */
export const NewOccupantStatementSchema = z.object({
    /** The prior occupant, if known — helps ops match the hold. Optional. */
    priorOccupantName: z.string().min(1).max(200).optional(),
    /** The customer's move-in date at the premise. */
    moveInDate: IsoDateSchema,
    /** How the customer occupies the premise. */
    occupancyType: z.enum(['OWN', 'RENT', 'OTHER']),
    /** Affirmative attestation of the statement's truth (required). */
    acknowledged: z.literal(true),
});
/** `POST /bff/status/:token/new-occupant` request. */
export const NewOccupantRequestSchema = z.object({
    statement: NewOccupantStatementSchema,
    proof: OccupancyProofDocumentSchema,
});
/**
 * `POST /bff/status/:token/new-occupant` result. Success-only: a failure (bad
 * token, not on hold, ops-case endpoint not wired) is a non-2xx ProblemDetails,
 * never a "declined" success branch. No PII, no tenant, no ids — just an opaque
 * case reference the customer can quote to support.
 */
export const NewOccupantResponseSchema = z.object({
    status: z.literal('received'),
    /** Opaque CRM ops-case reference (e.g. `OPS-…`). Not enumerable, not an account id. */
    caseReference: z.string().min(1).max(120),
});
//# sourceMappingURL=new-occupant.js.map